// useMe — the ONE real query in phase 1.
//
// Hydrates the user-chip in the sidebar, the role-aware nav variant in App,
// and the "logged in as X" copy on the dashboard. Phase 2 will add more queries
// alongside this one.
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { getMe } from "@/api/auth";
import { useAuth, isAdminRole, type Me } from "@/store/auth";
import { useUI } from "@/store/ui";

export function useMe() {
  const status = useAuth((s) => s.status);
  const setUser = useAuth((s) => s.setUser);
  const setCurrentMerchant = useUI((s) => s.setCurrentMerchant);

  const q = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    enabled: status === "authenticated",
    staleTime: 5 * 60_000,
    // Background refresh so role downgrades propagate without a manual
    // reload. The 5-min cadence catches scheduled changes; the response
    // interceptor in api/client.ts also force-invalidates ['me'] on any
    // 403 (audit-pass-4 H-4), which closes the read-side window the
    // moment the server first rejects a stale-role action.
    //
    // refetchOnWindowFocus is omitted — the 403 interceptor is the
    // event-driven path, and the global QueryClient default already
    // disables focus refetch to avoid double-fire on tab activation
    // (audit-pass-4 H-5).
    refetchInterval: 5 * 60_000,
    refetchIntervalInBackground: false,
  });

  // Mirror query result into the auth store so non-query code (sidebar,
  // StrataTweaks, etc.) can read user without subscribing to react-query.
  //
  // Also validates the rehydrated currentMerchantId against the server-
  // authoritative memberships[]. If the stored ID is stale or not in the
  // user's memberships, it is re-picked from memberships[0].
  //
  // CRITICAL: this validation runs ONCE per /v1/me fetch (deps = [q.data,...]).
  // currentMerchantId is read via useUI.getState() — putting it in the deps
  // creates an infinite ping-pong with MerchantSwitcher's auto-select for
  // admins (admin has no memberships, useMe resets to null, switcher re-picks
  // from the merchant list, useMe re-runs, resets again, …). For admins, we
  // do not reset at all here — the switcher owns the choice.
  useEffect(() => {
    if (!q.data) return;
    setUser(q.data);

    const stored = useUI.getState().currentMerchantId;
    const membershipIds = q.data.memberships.map((m) => m.merchant_id);
    const isValidMerchant = stored != null && membershipIds.includes(stored);
    const isAdmin = isAdminRole(q.data.role);

    if (!isValidMerchant && q.data.memberships.length > 0) {
      // Merchant user with a stale/null id — re-pick from first membership.
      // Carry the slug too so the X-Merchant-ID header and URL builders
      // can prefer it over the int id (backend accepts both, but slugs
      // read better in logs / URLs).
      const first = q.data.memberships[0];
      setCurrentMerchant(first.merchant_id, first.slug ?? null);
    } else if (!isValidMerchant && !isAdmin) {
      // Non-admin with no memberships — explicitly null.
      setCurrentMerchant(null, null);
    }
    // Admin path: leave currentMerchantId alone. MerchantSwitcher auto-selects
    // from the admin merchants list and persists across reloads and tabs via
    // localStorage. Resetting here would race that selection forever.
  }, [q.data, setUser, setCurrentMerchant]);

  return q;
}

export type UseMeResult = ReturnType<typeof useMe>;
export type { Me };
